import i18n from 'i18next';
import type { HeliosCellDataExtended } from 'types/helios/agendaListing';
import type { DataRecordColumn } from 'types/dataHelios';
import { isEmpty } from './filterFormUtils';
import { isHeliosNumericDataType, parseHeliosNumericRawValue } from './numberFormatting';
import { isObjectEmpty } from '../typeChecks';

type BrowseUpdatedEntries = Array<{ id: string; status: 'add' | 'remove' }>;

export const countHeliosBrowseItems = (cellData: HeliosCellDataExtended | DataRecordColumn, fieldValue: BrowseUpdatedEntries | unknown): number => {
  const originalEntries =
    cellData?.columnType === 'RelationDynamic' ? (cellData?.dynamicRelationValuesCount ?? 0) : cellData?.relationStaticValue ? 1 : 0;
  const addedRemovedEntries = Array.isArray(fieldValue) ? (fieldValue as BrowseUpdatedEntries) : [];
  const { addedEntries, removedEntries } = addedRemovedEntries.reduce(
    (statusAggregatedEntries, currentEntry) => {
      if (typeof currentEntry === 'object' && currentEntry !== null && 'status' in currentEntry) {
        if (currentEntry.status === 'add') {
          statusAggregatedEntries.addedEntries.push(currentEntry);
        } else if (currentEntry.status === 'remove') {
          statusAggregatedEntries.removedEntries.push(currentEntry);
        }
      }
      return statusAggregatedEntries;
    },
    {
      addedEntries: [] as BrowseUpdatedEntries,
      removedEntries: [] as BrowseUpdatedEntries,
    }
  );
  return originalEntries + addedEntries.length - removedEntries.length;
};

interface NumberRange {
  minValue?: number | null;
  maxValue?: number | null;
}

export const validateHeliosNumberRange = (value: unknown, dataType: string, range?: NumberRange | null): string | true => {
  if (!isHeliosNumericDataType(dataType) || (range?.minValue == null && range?.maxValue == null)) {
    return true;
  }

  const numericValue = parseHeliosNumericRawValue(value, dataType);
  if (numericValue === null) {
    return true;
  }

  const minValue = range?.minValue;
  const maxValue = range?.maxValue;
  const outOfRange = (minValue != null && numericValue < minValue) || (maxValue != null && numericValue > maxValue);
  if (!outOfRange) {
    return true;
  }

  if (minValue != null && maxValue != null) {
    return i18n.t('common.rangeValue', { min: minValue, max: maxValue });
  }

  return minValue != null ? `${i18n.t('common.minValue')}${minValue}` : `${i18n.t('common.maxValue')}${maxValue}`;
};

export const isHeliosValueEmpty = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.trim().length === 0;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime());
  }

  if (isEmpty(value)) {
    return true;
  }

  if (typeof value === 'object') {
    return isObjectEmpty(value);
  }

  return false;
};
